<script setup lang="ts">
    import { useDisplay, useTheme } from 'vuetify';
    import AppMenu from '@/components/menu/AppMenu.vue';
    import AppMenuMobile from '@/components/menu/AppMenuMobile.vue';
    import WindowManager from '@/components/window/WindowManager.vue';
    import WindowTaskbar from '@/components/window/WindowTaskbar.vue';
    import { PAGE_SEARCH_KEY, usePageSearch } from '@/composables/usePageSearch';
    import { usePinchZoom } from '@/composables/usePinchZoom';
    import { EventName } from '@/constants';
    import { snackbarMessages } from '@/helpers/message';
    import { applyPwaUpdate, dismissPwaUpdate, pwaUpdateAvailable } from '@/pwa/registerServiceWorker';
    import eventBus from '@/services/eventBus';
    import { useAppStore } from '@/stores/app';
    import { useMenuStore } from '@/stores/menu';
    import { useWebSocketStore } from '@/stores/webSocket';

    const { t } = useI18n();
    const menuStore = useMenuStore();
    const appStore = useAppStore();
    const wsStore = useWebSocketStore();
    const theme = useTheme();
    const display = useDisplay();
    const route = useRoute();

    const pageSearch = usePageSearch();
    provide(PAGE_SEARCH_KEY, pageSearch);

    watch(
        () => route.fullPath,
        () => {
            if (appStore.searchActive) {
                pageSearch.reindex();
            }
        }
    );

    // Initialize pinch-to-zoom only on mobile devices (works like browser zoom settings)
    const isMobile = display.mobile.value || 'ontouchstart' in window;
    const scale = ref(1);
    const isZooming = ref(false);

    if (isMobile) {
        const pinchZoom = usePinchZoom(0.35, 1.0);
        scale.value = pinchZoom.scale.value;
        isZooming.value = pinchZoom.isZooming.value;
        watch(
            () => pinchZoom.scale.value,
            (newVal) => {
                scale.value = newVal;
            }
        );
        watch(
            () => pinchZoom.isZooming.value,
            (newVal) => {
                isZooming.value = newVal;
            }
        );
    }

    watch(
        () => appStore.currentTheme,
        (newVal) => {
            theme.change(newVal);
        }
    );

    const handleGlobalSearch = (event: KeyboardEvent) => {
        if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
            if (menuStore.withMenu) {
                event.preventDefault();
                if (!appStore.searchActive) {
                    pageSearch.openSearch();
                }
            }
        }
    };

    onMounted(() => {
        window.addEventListener('keydown', handleGlobalSearch);

        window.addEventListener('focus', () => {
            wsStore.checkConnection();
        });

        document.addEventListener('visibilitychange', () => {
            console.log(document.hidden ? 'Tab is Inactive' : 'Tab is Active');

            appStore.isBrowserTabInactive = document.hidden;

            if (!document.hidden) {
                const browserTabInactiveTimeSec = (Date.now() - (appStore.lastBrowserTabInactive ?? 0)) / 1000;
                appStore.lastBrowserTabInactive = null;
                eventBus.emit(EventName.BrowserTabVisibilityChanged, { isBrowserTabInactive: false, browserTabInactiveTimeSec: browserTabInactiveTimeSec });
            } else {
                eventBus.emit(EventName.BrowserTabVisibilityChanged, { isBrowserTabInactive: true });
                appStore.lastBrowserTabInactive = Date.now();
                wsStore.checkConnection();
            }
        });
    });

    onBeforeUnmount(() => {
        window.removeEventListener('keydown', handleGlobalSearch);
    });
</script>

<template>
    <v-app ref="main" id="mainId">
        <AppMenuMobile v-if="menuStore.withMenu" />

        <AppMenu v-if="menuStore.withMenu"></AppMenu>

        <v-main class="d-flex flex-column main-content" :class="menuStore.withMenu ? 'mt-12 pt-0' : 'pt-0'">
            <WindowTaskbar />

            <router-view v-slot="{ Component }">
                <div v-if="Component" class="page-view d-flex flex-column flex-grow-1 min-height-0">
                    <component :is="Component" />
                </div>
            </router-view>
        </v-main>

        <WindowManager />

        <v-snackbar-queue v-model="snackbarMessages" :content-props="{ class: 'tb-toast' }" display-strategy="hold" :gap="8" :total-visible="4" collapsed />

        <v-snackbar
            v-model="pwaUpdateAvailable"
            color="toastUpdate"
            location="top center"
            rounded="xl"
            :timeout="-1"
            :content-props="{ class: 'tb-toast tb-toast-update' }"
        >
            <div class="tb-toast-content">
                <v-icon class="mr-2" color="white" :icon="mdiReloadAlert" size="20" />
                {{ t('pwa.newVersionAvailable') }}
            </div>

            <template #actions>
                <v-btn class="tb-toast-update__reload" variant="flat" @click="applyPwaUpdate">
                    {{ t('action.reload') }}
                </v-btn>
                <v-btn class="tb-toast-update__close" color="white" variant="text" density="compact" size="small" :icon="mdiClose" @click="dismissPwaUpdate" />
            </template>
        </v-snackbar>

        <!-- Zoom indicator -->
        <Transition name="zoom-indicator-fade">
            <div v-if="isZooming" class="zoom-indicator">{{ Math.round(scale * 100) }}%</div>
        </Transition>
    </v-app>
</template>

<style lang="scss">
    #mainId {
        height: 100vh;
        height: 100dvh;
        overflow: hidden;
    }

    .main-content {
        position: relative;
        flex: 1 1 0 !important;
        overflow: hidden !important;
        min-height: 0 !important;
        background: rgb(var(--v-theme-surface));
    }

    .page-view {
        min-height: 0;
    }

    .zoom-indicator {
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: rgba(0, 0, 0, 0.8);
        color: white;
        padding: 16px 24px;
        border-radius: 12px;
        font-size: 18px;
        font-weight: 600;
        z-index: 10000;
        pointer-events: none;
        display: flex;
        align-items: center;
        gap: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        backdrop-filter: blur(8px);
    }

    .v-theme--dark .zoom-indicator {
        background: rgba(255, 255, 255, 0.15);
        color: white;
    }

    .zoom-indicator-fade-enter-active,
    .zoom-indicator-fade-leave-active {
        transition: opacity 0.3s ease;
    }

    .zoom-indicator-fade-enter-from,
    .zoom-indicator-fade-leave-to {
        opacity: 0;
    }
</style>
